import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { tap, catchError, of, Observable, lastValueFrom } from 'rxjs';
import { adminEnvironment } from '../../../../environments/environment';
import { SessionService } from '../../../services/session-management.service'
import { LocalStorageService } from '../../../services/local-storage.service'
import { LoaderService } from './loader.service';
import { Category } from '../manage-product/manage-product.component';
import { Channel, Customer, InventoryEntry, RolePolicy, Tenant, Variant, VariantNew } from '../models/combined-interfaces.model';
import { ProductGroup } from './product-group.service';
import { FeedMetadataUpdateRequest, FeedOverride } from '../../../../models/feed-override.model';
import { FeedListItem } from '../../../../models/feed-list-item.model';
import { TenantSummary } from '../models/tenant-admin.model';



@Injectable({
  providedIn: 'root',
})
export class AdminCommonHttpService {
  public token: any;

  constructor(
    private http: HttpClient,
    private sessionManager: SessionService,
    private localStorage: LocalStorageService,
    private loaderService: LoaderService
  ) { } // No need to reassign http

  private getHeaders(
    sessionToken?: string,
    requireAuth: boolean = true
  ): { [header: string]: string } {
    const headers: { [header: string]: string } = {};

    if (requireAuth) {
      const token = this.localStorage.getItem('access_token');
      if (!token) {
        console.log('No access token found');
        return headers;
      }
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (sessionToken) {
      headers['x-session-token'] = sessionToken;
    }


    return headers;
  }


  async getCategories(): Promise<any[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/category/get-category-tree`,
          { sessionToken: this.localStorage.getItem('x-session-token') },
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-admin-categories:', response);
      return response;
    } catch (error) {
      console.error('Error getting customer ID:', error);
      return [];
    }
  }

  async getAllProducts(limit: number = 10, offset: number = 0, orderBy: string = 'created_at', descending: boolean = true): Promise<any[]> {
    const queryParams: any = {
      limit: limit.toString(),
      offset: offset.toString(),
      order_by: orderBy,
      descending: descending.toString(),
      sessionToken: this.localStorage.getItem('x-session-token')
    };

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/product/get-all-products`,
          queryParams,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-all-products:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  async getAllProductsNoPagination(): Promise<any[]> {
    const queryParams: any = {};

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/product/get-all-products-no-pagination`,
          queryParams,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-all-products-no-pagination:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  async deleteProduct(productId: number): Promise<any> {
    const payload = { product_id: productId };

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/product/soft-delete-product`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('delete-product:', response);
      return response;
    } catch (error) {
      console.error('Error deleting product:', error);
      return [];
    }
  }

  async updateProduct(productId: number, payload: any): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPut(
          `admin/product/update-product`,
          { product_id: productId, ...payload },
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('update-product:', response);
      return response;
    } catch (error) {
      console.error('Error updating product:', error);
      return [];
    }
  }


  async restoreProduct(payload: any): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPut(
          `admin/product/restore-product`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('add-product:', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }

  async addProduct(payload: any): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/product/add-product`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('add-product:', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }


  async addCategory(payload: any): Promise<any[]> {
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/category/save-category`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('add-admin-category:', response);
      return response;
    } catch (error) {
      console.error('Error getting customer ID:', error);
      return [];
    }
  }

  async deleteCategory(categoryId: any): Promise<any[]> {
    const payload = { category_id: categoryId };
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/category/delete-category`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-admin-categories:', response);
      return response;
    } catch (error) {
      console.error('Error getting customer ID:', error);
      return [];
    }
  }

  async restoreCategory(categoryId: any): Promise<any[]> {
    const payload = { category_id: categoryId };
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/category/restore-category`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-admin-categories:', response);
      return response;
    } catch (error) {
      console.error('Error getting customer ID:', error);
      return [];
    }
  }


  async unlinkProductFromCategories(payload: { product_id: number; category_ids: number[] }): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/product/unlink-product-categories`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-admin-categories:', response);
      return response;
    } catch (error) {
      console.error('Error getting customer ID:', error);
      return [];
    }
  }


  async getCategoriesForProduct(productId: any): Promise<any[]> {
    const queryParams: any = {
      product_id: productId
    };

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/category/get-categories-for-product`,
          queryParams,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-categories-for-product:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  async linkProductToCategories(payload: { product_id: number; category_ids: number[] }): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/product/link-product-categories`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-admin-categories:', response);
      return response;
    } catch (error) {
      console.error('Error getting customer ID:', error);
      return [];
    }
  }

  async getAllCategories(): Promise<Category[]> {
    const queryParams = {}
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/category/get-all-categories`,
          queryParams,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-all-products:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  async getVariantsByProduct(productId: any): Promise<Variant[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/product-variant/get-variants-by-product/${productId}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-variants-by-product response:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  async getProductById(productId: any): Promise<VariantNew[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/product/get-product/${productId}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-variants-by-product response:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  async getVariantById(variantId: any): Promise<Variant | null> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/product-variant/get-variant/${variantId}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-variants-by-product response:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return null;
    }
  }

  async getAllChannels(): Promise<Channel[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/channels/get-all-channels`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-all-channels:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }


  async getInventoryByVariant(variant_id: any): Promise<InventoryEntry[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/inventory/get-by-variant/${variant_id}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get-variants-by-product response:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  async getTenant(): Promise<Tenant | null> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/tenant-service/get-tenant`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get tenant response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return null;
    }
  }

  async getTenantUsers(): Promise<any | null> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/users`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get tenant response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return null;
    }
  }

  async getTenantList(): Promise<TenantSummary[] | []> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/tenant-service/list`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get tenant response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return [];
    }
  }

  async disableUser(payload: any): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/users/disable`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get tenant response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return null;
    }
  }

  async enableUser(payload: any): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/users/enable`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get tenant response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return null;
    }
  }

  async inviteUser(payload: any): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/users/invite`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get tenant response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return null;
    }
  }

  async createTenant(payload: any): Promise<any[] | []> {

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/tenant-service/create-tenant`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get tenant response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return [];
    }
  }

  async suspendTenant(payload: any): Promise<any[] | []> {

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/tenant-service/suspend-tenant`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get tenant response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return [];
    }
  }

  async activateTenant(payload: any): Promise<any[] | []> {

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/tenant-service/activate-tenant`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get tenant response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return [];
    }
  }

  async rotateTenantKey(payload: any): Promise<any> {

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/tenant-service/api-keys/rotate`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('rotate key response:', response);
      return response;
    } catch (error) {
      console.error('Error getting tenant:', error);
      return [];
    }
  }


  async getAllProductsInGroup(group_id: any): Promise<any[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/product-group/${group_id}/products`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get all products in group response:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  async getVariantKeys(): Promise<any[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/product-variant/get-variant-keys`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('get all products in group response:', response);
      return response;
    } catch (error) {
      console.error('Error getting products:', error);
      return [];
    }
  }

  async updateVariantAttributes(variantId: number, attributes: any): Promise<any> {
    const payload = {
      variant_id: variantId,
      attributes: attributes
    };
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/product-variant/update-attributes`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Variants Updated:', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }

  async createProductGroup(groupName: string, description: string): Promise<any> {
    const payload = {
      group_name: groupName,
      description: description
    }

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/product-group/create-product-group`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Variants Updated:', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }

  async updateProductGroup(group_id: string, groupName: string, description: string): Promise<any> {
    const payload = {
      group_name: groupName,
      description: description
    }

    try {
      const response = await lastValueFrom(
        this.commonPut(
          `admin/product-group/update-product-group/${group_id}`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Variants Updated:', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }

  async assignProductToGroup(product_id: string, product_group_id: string): Promise<any> {
    const payload = {
      product_group_id: product_group_id,
    }

    try {
      const response = await lastValueFrom(
        this.commonPut(
          `admin/product/${product_id}/group`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Variants Updated:', response);

      return response;
    } catch (error) {
      console.error('Assigned Product to Group:', error);
      return [];
    }
  }

  async assignProductsToGroup(product_ids: string, group_ids: string): Promise<any> {
    const payload = {
      product_ids: product_ids,
      group_ids: group_ids,
    }

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/product-groups/assign-products`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Assigned Products to Group:', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }

  async deleteProductGroup(group_id: string): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonDelete(
          `admin/product-group/delete-product-group/${group_id}`,
          null,
          'json'
        )
      );
      console.log('Delete Product Group:', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }

  async getAllGroups(): Promise<ProductGroup[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `/admin/product-group/get-product-groups`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get All Groups :', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }

  async getRbacRoles(): Promise<string | null> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          'admin/rbac/get-role-access',
          {},
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get Rbac Roles :', response);

      return response ?? null;
    } catch (error) {
      console.error('Error getting RBAC roles:', error);
      return null;
    }
  }

  async getRbacRolePermissions(): Promise<RolePolicy[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          'admin/rbac/get-role-permissions',
          {},
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get Rbac Role Permissions :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return [];
    }
  }

  async getAllRolePermissions(): Promise<RolePolicy[]> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          'admin/rbac/get-all-role-permissions',
          {},
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get Rbac Role Permissions :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return [];
    }
  }

  async setRbacRolePermissions(role: any, permission: any): Promise<any> {
    const payload = { role: permission };
    try {
      const response = await lastValueFrom(
        this.commonGet(
          'admin/rbac/set-role-access',
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Set RBAC Role Permissions :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return [];
    }
  }

  async deleteRbacRole(role: any): Promise<any> {

    try {
      const response = await lastValueFrom(
        this.commonDelete(
          'admin/rbac/delete-role',
          null,
          role
        )
      );
      console.log('Set RBAC Role Permissions :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return [];
    }
  }

  async createProductVariant(payload: any): Promise<any> {

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/product-variant/create-product-variant`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Assigned Products to Group:', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }


  async deleteVariant(variant_id: any): Promise<any> {

    try {
      const response = await lastValueFrom(
        this.commonDelete(
          `admin/product-variant/delete-variant/${variant_id}`,
          null,
          null
        )
      );
      console.log('Deleted Variant:', response);

      return response;
    } catch (error) {
      console.error('Error adding product:', error);
      return [];
    }
  }



  async getAllCustomers(): Promise<Customer[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          '/admin/customer/get-all-customers',
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get Rbac Role Permissions :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return [];
    }
  }

  async getAllOrders(): Promise<any[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          'admin/orders',
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get Rbac Role Permissions :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return [];
    }
  }

  async getOrdersByCustomerId(customer_id: any): Promise<any[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/customers/{${customer_id}}/orders`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get Rbac Role Permissions :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return [];
    }
  }

  async getOrderById(order_id: any): Promise<any[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/orders/${order_id}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get Rbac Role Permissions :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting role permissions:', error);
      return [];
    }
  }

  async getFeedDestinations(): Promise<any[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/feed/destinations`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get feed destinations :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting feed destinations permissions:', error);
      return [];
    }
  }

  async connectFeedAccount(payload: any): Promise<any> {

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/feed/connect`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Created feed connection:', response);

      return response;
    } catch (error) {
      console.error('Error creating feed account connection:', error);
      return [];
    }
  }

  async saveMappings(payload: any): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/feed-mappings/tenant`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Update Mappings:', response);

      return response;
    } catch (error) {
      console.error('Error creating feed account connection:', error);
      return [];
    }
  }

  async getTenantFeedAccounts(): Promise<any[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/feed/accounts`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get feed destinations :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting feed destinations permissions:', error);
      return [];
    }
  }

  async getTenantFieldMappings(destination_code: string): Promise<any[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/feed-mappings/tenant/${destination_code}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get feed destinations :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting feed destinations permissions:', error);
      return [];
    }
  }

  async getFieldMappingsForDestination(destination_code: string): Promise<any[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/feed-mappings/tenant/${destination_code}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get feed destinations :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting feed destinations permissions:', error);
      return [];
    }
  }



  async disconnectFeedAccount(destinationCode: any): Promise<any> {
    const payload = `{"destination_code":"${destinationCode}"}`
    try {
      const response = await lastValueFrom(
        this.commonDelete(
          `admin/feed/disconnect`,
          payload,
          null
        )
      );
      console.log('Deleted Variant Key:', response);

      return response;
    } catch (error) {
      console.error('Error deleting variant key:', error);
      return [];
    }
  }

  async createVariantKey(variant_key: string, variant_key_name: string): Promise<any> {
    const payload = {
      variant_key: variant_key,
      variant_key_name: variant_key_name
    }

    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/product-variant/create-variant-key`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Variant Created:', response);

      return response;
    } catch (error) {
      console.error('Error creating variant:', error);
      return [];
    }
  }

  async deleteVariantKey(variant_key: any): Promise<any> {

    try {
      const response = await lastValueFrom(
        this.commonDelete(
          `admin/product-variant/delete-variant-key/${variant_key}`,
          null,
          null
        )
      );
      console.log('Deleted Variant Key:', response);

      return response;
    } catch (error) {
      console.error('Error deleting variant key:', error);
      return [];
    }
  }

  async getFeedMetadataByProductId(product_id: string): Promise<FeedOverride[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/feed-metadata/${product_id}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get feed destinations :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting feed destinations permissions:', error);
      return [];
    }
  }

  async getFeedMetadataList(): Promise<FeedListItem[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/feed-metadata/list`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get feed destinations :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting feed destinations permissions:', error);
      return [];
    }
  }

  async getSourceFields(): Promise<FeedListItem[]> {

    try {
      const response = await lastValueFrom(
        this.commonGet(
          `/admin/feed/source-fields`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Get Source Fields :', response);
      return response ?? [];
    } catch (error) {
      console.error('Error getting getting source fields:', error);
      return [];
    }
  }

  async upsertFeedMetadataByProductId(product_id: string, payload: FeedMetadataUpdateRequest): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonPost(
          `admin/feed-metadata/${product_id}`,
          payload,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Feed Metadata Overrides Updated:', response);

      return response;
    } catch (error) {
      console.error('Assigned Product to Group:', error);
      return [];
    }
  }

  async getFeedOverrideFieldConfig(destination: string): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `/admin/feed-metadata/destination/fields/${destination}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Variants Updated:', response);

      return response;
    } catch (error) {
      console.error('Assigned Product to Group:', error);
      return [];
    }
  }

  async getOverridesForProductAndDestination(productId: string, destination: string): Promise<any> {
    try {
      const response = await lastValueFrom(
        this.commonGet(
          `admin/feed-metadata/${productId}/destination?destination=${destination}`,
          null,
          'json',
          null ?? undefined,
          false
        )
      );
      console.log('Getting overrides for product Destination : ', response);

      return response;
    } catch (error) {
      console.error('Assigned Product to Group:', error);
      return [];
    }
  }

  async getFeedMetadataByProductIdAndDestination(productId: string, destination: string): Promise<any> {
    try {

      const url = `admin/feed-metadata/${productId}/destination?destination=${destination}`;
      const response = await lastValueFrom(this.commonGet(
        url,
        null,
        'json',
        null ?? undefined,
        false
      ));


      console.log('Variants Updated:', response);

      return response;
    } catch (error) {
      console.error('Assigned Product to Group:', error);
      return [];
    }
  }

  // Common API call function
  private commonGet(
    apiEndpoint: string,
    queryParams?: any,
    responseType: any = 'text',
    sessionToken?: string,
    requireAuth: boolean = true
  ): Observable<any> {
    this.loaderService.show();
    const headers = this.getHeaders(sessionToken, requireAuth);
    if (Object.keys(headers).length === 0 && requireAuth) {
      return of(null); // Return an observable emitting null if headers are empty and auth is required
    }

    console.log(`HEADERS :: ${JSON.stringify(headers)}`);

    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.http
      .get(
        `${adminEnvironment.aws.apiGateway.domain}/${adminEnvironment.aws.apiGateway.env}/${apiEndpoint}`,
        {
          headers,
          params,
          responseType,
        }
      )
      .pipe(
        tap((response) => {
          this.loaderService.hide();
          console.log('Response from API:', response);
        }),
        catchError((error) => {
          console.error('Error occurred during GET request:', error);
          this.loaderService.hide();
          return of(null);
        })
      );
  }


  private commonPost(
    apiEndpoint: string,
    body: any,
    responseType: any = 'text',
    sessionToken?: string,
    requireAuth: boolean = true,
    queryParams?: any
  ): Observable<any> {
    this.loaderService.show();
    const headers = this.getHeaders(sessionToken, requireAuth);
    if (Object.keys(headers).length === 0 && requireAuth) {
      return of(null);
    }

    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.http
      .post(
        `${adminEnvironment.aws.apiGateway.domain}/${adminEnvironment.aws.apiGateway.env}/${apiEndpoint}`,
        body,
        {
          headers,
          responseType,
          params
        }
      )
      .pipe(
        tap((response) => {
          console.log('Response from API:', response);
          this.loaderService.hide();
        }),
        catchError((error) => {
          console.error('Error occurred during POST request:', error);
          this.loaderService.hide();
          return of(null);
        })
      );
  }

  private commonPut(
    apiEndpoint: string,
    body: any,
    responseType: any = 'text',
    sessionToken?: string,
    requireAuth: boolean = true,
    queryParams?: any
  ): Observable<any> {
    this.loaderService.show();
    const headers = this.getHeaders(sessionToken, requireAuth);
    if (Object.keys(headers).length === 0 && requireAuth) {
      return of(null);
    }

    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.http
      .put(
        `${adminEnvironment.aws.apiGateway.domain}/${adminEnvironment.aws.apiGateway.env}/${apiEndpoint}`,
        body,
        {
          headers,
          responseType,
          params
        }
      )
      .pipe(
        tap((response) => {
          this.loaderService.hide();
          console.log('Response from API:', response);
        }),
        catchError((error) => {
          this.loaderService.hide();
          console.error('Error occurred during PUT request:', error);
          return of(null); // Return an observable emitting null on error
        })
      );
  }

  private commonDelete(
    apiEndpoint: string,
    body: any,
    queryParams?: any
  ): Observable<any> {
    this.loaderService.show();

    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach((key) => {
        params = params.set(key, queryParams[key]);
      });
    }

    return this.http
      .delete(
        `${adminEnvironment.aws.apiGateway.domain}/${adminEnvironment.aws.apiGateway.env}/${apiEndpoint}`,
        {
          body,
          params,
        }
      )
      .pipe(
        tap((response) => {
          this.loaderService.hide();
          console.log('Response from API:', response);
        }),
        catchError((error) => {
          this.loaderService.hide();
          console.error('Error occurred during DELETE request:', error);
          return of(null);
        })
      );
  }

}
